import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import {
  getPurchases, reorderPurchasePriority, getPurchasePriorityCut, getPrioridadPedidos,
  getPrioridadPedidosCandidatos, agregarPedidoPrioridad, quitarPedidoPrioridad,
} from '../api/client'
import { lineTotals } from '../utils/purchaseTotals'
import { currentLocale } from '../i18n'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'

const STATE_STYLE = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  sap:       'bg-violet-100 text-violet-700',
  rejected:  'bg-red-100 text-red-700',
  cancelled: 'bg-orange-100 text-orange-700',
  ordered:   'bg-indigo-100 text-indigo-700',
  received:  'bg-emerald-100 text-emerald-700',
  closed:    'bg-gray-200 text-gray-700',
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' })
}

const fmtMonto = (n, moneda) => `${Number(n).toLocaleString(currentLocale(), { maximumFractionDigits: 2 })} ${moneda}`

// What an entry costs: a request (IVA included; null when its lines are in different currencies) or a SAP order (CLP).
function costoDe(e) {
  if (e.tipo === 'pedido') return e.data.total_clp == null ? null : { moneda: 'CLP', total: e.data.total_clp }
  const tot = lineTotals(e.data)
  if (tot.mixed || tot.total == null) return null
  return { moneda: tot.currency || e.data.currency, total: tot.total }
}

// Sums by currency for the entries above or below the cut.
function sumar(lista) {
  const por = {}
  let sinCosto = 0
  for (const e of lista) {
    const c = costoDe(e)
    if (!c) { sinCosto += 1; continue }
    por[c.moneda] = (por[c.moneda] || 0) + c.total
  }
  return { por, sinCosto }
}

function Sumas({ s, t }) {
  const partes = Object.entries(s.por).map(([m, v]) => fmtMonto(v, m))
  if (s.sinCosto) partes.push(t('board.priority.sinCosto', { count: s.sinCosto }))
  return <span>{partes.length ? partes.join(' + ') : '—'}</span>
}

// The queue is one list of requests and SAP orders, by rank; what has no rank yet goes last, in the order it came.
function unir(solicitudes, pedidos) {
  const todos = [
    ...solicitudes.map(p => ({ tipo: 'solicitud', id: p.id, rank: p.priority_rank, data: p })),
    ...pedidos.map(p => ({ tipo: 'pedido', id: p.pedido_id, rank: p.priority_rank, data: p })),
  ]
  return todos.map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.rank == null) - (b.e.rank == null) || (a.e.rank ?? 0) - (b.e.rank ?? 0) || a.i - b.i)
    .map(x => x.e)
}

// Window to bring unpaid SAP orders into the queue.
function TraerPedidos({ onClose, onAgregado, t }) {
  const [q, setQ] = useState('')
  const [lista, setLista] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    let vivo = true
    const h = setTimeout(() => {
      getPrioridadPedidosCandidatos(q.trim()).then(r => { if (vivo) setLista(r) })
        .catch(e => { if (vivo) setErr(e.response?.data?.detail || e.message) })
    }, 300)
    return () => { vivo = false; clearTimeout(h) }
  }, [q])

  const agregar = async (p) => {
    setErr('')
    try {
      await agregarPedidoPrioridad(p.pedido_id)
      setLista(l => (l || []).filter(x => x.pedido_id !== p.pedido_id))
      onAgregado()
    } catch (e) { setErr(e.response?.data?.detail || e.message) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col p-5 gap-3" role="dialog" aria-label={t('board.priority.traer.title')}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('board.priority.traer.title')}</h2>
            <p className="text-sm text-gray-500">{t('board.priority.traer.hint')}</p>
          </div>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800">{t('board.priority.traer.close')} ✕</button>
        </div>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('board.priority.traer.search')}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" />
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="overflow-y-auto min-h-0 flex-1 border border-gray-100 rounded-lg">
          {lista == null ? <p className="p-4 text-sm text-gray-400">{t('common:actions.loading')}</p>
            : lista.length === 0 ? <p className="p-4 text-sm text-gray-400">{t('board.priority.traer.empty')}</p> : (
              <ul>
                {lista.map(p => (
                  <li key={p.pedido_id} className="px-3 py-2 border-t first:border-t-0 border-gray-100 flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-gray-500 w-16 shrink-0">{p.pedido_num}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-gray-900">{p.descripcion}{p.mas_lineas > 0 ? ` (+${p.mas_lineas})` : ''}</div>
                      <div className="text-xs text-gray-400 truncate">{p.proveedor || '—'} · {fmtDate(p.fecha_doc)}{p.proyectos_rem_txt || p.proyectos.length ? ` · ${p.proyectos_rem_txt || p.proyectos.join(', ')}` : ''}</div>
                    </div>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{p.total_clp == null ? '—' : fmtMonto(p.total_clp, 'CLP')}</span>
                    <button type="button" onClick={() => agregar(p)} className="px-3 py-1 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700">{t('board.priority.traer.add')}</button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  )
}

export default function PurchasePriorityQueue() {
  const { t } = useTranslation(['purchases', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const roles = user?.roles || (user?.role ? [user.role] : [])
  // Only c_level/admin may reorder — purchasing and PM/PO see the resulting
  // order (so they know what to buy first) but can't change it.
  const canReorder = roles.includes('admin') || roles.includes('c_level')

  const [solicitudes, setSolicitudes] = useState([])
  const [pedidos, setPedidos] = useState([])                // SAP orders brought into the queue
  const [corte, setCorte] = useState(null)                  // rank of the last entry above the cut; null: no cut
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [trayendo, setTrayendo] = useState(false)
  const zoom = useZoom('adq_zoom_cola')

  const cola = unir(solicitudes, pedidos)

  const load = async () => {
    setLoading(true)
    try {
      const [data, ped, c] = await Promise.all([
        getPurchases({ scope: 'priority' }),
        getPrioridadPedidos().catch(() => []),
        getPurchasePriorityCut().catch(() => ({ corte_rank: null })),
      ])
      setSolicitudes(Array.isArray(data) ? data : [])
      setPedidos(Array.isArray(ped) ? ped : [])
      setCorte(c.corte_rank ?? null)
    } catch (e) {
      setErr(e.response?.data?.detail || e.message)
      setSolicitudes([])
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  // How many are above the cut: the queue is in order, so they are the first ones.
  const nArriba = corte == null ? null : cola.filter(e => e.rank != null && e.rank <= corte).length

  // Saves an order (and where the cut goes) in one go.
  const guardar = async (ordenada, cutAfter) => {
    try {
      await reorderPurchasePriority(ordenada.map(e => ({ tipo: e.tipo, id: e.id })), cutAfter)
      await load()
    } catch (e) {
      setErr(e.response?.data?.detail || e.message)
    }
  }

  // Move one position up/down. Buttons (not drag) so this works from a phone —
  // native HTML5 drag-and-drop never fires on touch. The cut stays between the same two positions.
  const reorder = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= cola.length) return
    const next = [...cola]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    await guardar(next, nArriba ?? undefined)
  }

  const ponerCorte = (n) => guardar(cola, n)           // n entries above the cut
  const quitarCorte = () => guardar(cola, -1)

  const quitarPedido = async (e) => {
    try { await quitarPedidoPrioridad(e.id); await load() } catch (x) { setErr(x.response?.data?.detail || x.message) }
  }

  const arriba = nArriba == null ? [] : cola.slice(0, nArriba)
  const abajo = nArriba == null ? [] : cola.slice(nArriba)

  // The cut: above it is paid, below it is not. c_level and admin move it (or take it away).
  const lineaCorte = (
    <li key="corte" className="rounded-xl border-2 border-dashed border-red-300 bg-red-50/60 px-3 py-2 text-xs text-red-700 flex flex-wrap items-center justify-between gap-2" aria-label={t('board.priority.corte')}>
      <span className="font-semibold">✂ {t('board.priority.corteTitulo')}: <span className="font-normal">{t('board.priority.corteTexto')}</span></span>
      <span className="flex items-center gap-3">
        <span>{t('board.priority.sePaga')}: <strong><Sumas s={sumar(arriba)} t={t} /></strong></span>
        <span>{t('board.priority.noSePaga')}: <strong><Sumas s={sumar(abajo)} t={t} /></strong></span>
        {canReorder && <button type="button" onClick={quitarCorte} className="underline">{t('board.priority.quitarCorte')}</button>}
      </span>
    </li>
  )

  return (
    <div className="p-4 sm:p-6 flex flex-col h-[calc(100vh-9rem)]">
      <div className="mb-4 shrink-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('board.priority.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t(canReorder ? 'board.priority.subtitle' : 'board.priority.readonlyHint')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ControlZoom z={zoom} />
          {canReorder && (
            <button type="button" onClick={() => setTrayendo(true)}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              + {t('board.priority.traer.button')}
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 shrink-0">{err}</div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">{t('common:actions.loading')}</p>
      ) : cola.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">
          {t('board.priority.empty')}
        </div>
      ) : (
        <ZonaZoom z={zoom} className="flex-1 min-h-0">
        <ul className="flex flex-col gap-2 pb-4">
          {cola.map((e, idx) => {
            const p = e.data
            const esPedido = e.tipo === 'pedido'
            const costo = costoDe(e)
            const ir = () => navigate(esPedido ? `/compras/pedidos?abiertos=0&pedido=${p.pedido_id}` : `/purchases/${p.id}`)
            return (
              <Fragment key={`${e.tipo}-${e.id}`}>
                {nArriba === idx && lineaCorte}
                <li
                  role="button"
                  tabIndex={0}
                  onClick={ir}
                  onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ir() } }}
                  className="bg-white rounded-xl border border-gray-200 px-3 py-3 flex items-center gap-2 sm:gap-3 cursor-pointer hover:bg-gray-50/60 hover:border-gray-300 transition-colors"
                >
                  <span className="text-xs font-mono text-gray-400 w-7 shrink-0 text-center">{t('board.priority.rank', { n: idx + 1 })}</span>

                  {canReorder && (
                    <div className="flex flex-col shrink-0">
                      <button
                        type="button"
                        onClick={ev => { ev.stopPropagation(); reorder(idx, idx - 1) }}
                        disabled={idx === 0}
                        aria-label={t('board.priority.moveUp')}
                        className="text-gray-400 hover:text-gray-900 disabled:opacity-25 disabled:hover:text-gray-400 leading-none px-1"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={ev => { ev.stopPropagation(); reorder(idx, idx + 1) }}
                        disabled={idx === cola.length - 1}
                        aria-label={t('board.priority.moveDown')}
                        className="text-gray-400 hover:text-gray-900 disabled:opacity-25 disabled:hover:text-gray-400 leading-none px-1"
                      >
                        ▼
                      </button>
                    </div>
                  )}

                  {esPedido
                    ? <span className="text-[11px] font-semibold text-violet-700 bg-violet-100 rounded px-1.5 py-0.5 shrink-0">{t('board.priority.pedidoSap')} {p.pedido_num}</span>
                    : <span className="text-xs font-mono text-gray-400 shrink-0">#{p.id}</span>}

                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate text-sm">
                      {esPedido
                        ? `${p.descripcion}${p.mas_lineas > 0 ? ` (+${p.mas_lineas})` : ''}`
                        : (p.is_multi ? t('list.multipleItems', { count: (p.items || []).length }) : (p.item_description || '—'))}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {esPedido
                        ? `${p.proveedor || '—'} · ${p.proyectos_rem_txt || p.proyectos.join(', ') || '—'}${p.a_credito ? ` · ${t('board.priority.credito')}` : ''}`
                        : `${p.project_reference || '—'} · ${p.requester?.display_name || '—'}${p.tracking ? ` · ${p.tracking}` : ''}`}
                    </div>
                  </div>

                  <div className="text-right shrink-0 min-w-[6rem]">
                    {costo
                      ? <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">{fmtMonto(costo.total, costo.moneda)}</div>
                      : <div className="text-xs text-gray-400">{t('list.mixed')}</div>}
                    {canReorder && nArriba !== idx + 1 && (
                      <button type="button" onClick={ev => { ev.stopPropagation(); ponerCorte(idx + 1) }} className="text-[11px] text-gray-400 hover:text-red-600" title={t('board.priority.corteAqui')}>
                        ✂ {t('board.priority.corteAqui')}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {esPedido ? (
                      <>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{t('board.priority.sinPagar')}</span>
                        {canReorder && (
                          <button type="button" onClick={ev => { ev.stopPropagation(); quitarPedido(e) }} className="text-[11px] text-gray-400 hover:text-red-600">{t('board.priority.quitarPedido')}</button>
                        )}
                      </>
                    ) : (
                      <>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[p.state]}`}>
                          {t(`states.${p.state}`) || p.state}
                        </span>
                        {p.needed_by && <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmtDate(p.needed_by)}</span>}
                      </>
                    )}
                  </div>
                </li>
              </Fragment>
            )
          })}
          {nArriba != null && nArriba === cola.length && lineaCorte}
        </ul>
        </ZonaZoom>
      )}
      {trayendo && <TraerPedidos onClose={() => setTrayendo(false)} onAgregado={load} t={t} />}
    </div>
  )
}
